/**
 * Motion utilities for handling animations with accessibility considerations
 */

import { useEffect, useState } from 'react'

/**
 * Hook to detect if user prefers reduced motion
 */
export function useReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  return prefersReducedMotion
}

/**
 * Animation presets that respect reduced motion preferences
 */
export const motionPresets = {
  // Slide in from bottom
  slideUp: (prefersReduced: boolean) => ({
    initial: prefersReduced ? { opacity: 0 } : { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: prefersReduced ? 0.1 : 0.5, ease: 'easeOut' }
  }),

  // Slide in from left
  slideLeft: (prefersReduced: boolean) => ({
    initial: prefersReduced ? { opacity: 0 } : { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: prefersReduced ? 0.1 : 0.5, ease: 'easeOut' }
  }),

  // Slide in from right
  slideRight: (prefersReduced: boolean) => ({
    initial: prefersReduced ? { opacity: 0 } : { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: prefersReduced ? 0.1 : 0.5, ease: 'easeOut' }
  }),

  // Fade in
  fadeIn: (prefersReduced: boolean) => ({
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: prefersReduced ? 0.1 : 0.3 }
  }),

  // Scale in
  scaleIn: (prefersReduced: boolean) => ({
    initial: prefersReduced ? { opacity: 0 } : { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: prefersReduced ? 0.1 : 0.4, ease: 'easeOut' }
  }),

  // Stagger children
  stagger: (prefersReduced: boolean) => ({
    animate: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.1
      }
    }
  })
}

/**
 * Hover effects that respect reduced motion
 */
export const hoverPresets = {
  // Subtle lift
  lift: (prefersReduced: boolean) => 
    prefersReduced ? {} : {
      whileHover: { y: -2, scale: 1.02 },
      transition: { duration: 0.2 }
    },

  // Scale effect
  scale: (prefersReduced: boolean) =>
    prefersReduced ? {} : {
      whileHover: { scale: 1.05 },
      whileTap: { scale: 0.98 },
      transition: { duration: 0.2 }
    },

  // Glow effect (for buttons)
  glow: (prefersReduced: boolean) =>
    prefersReduced ? {} : {
      whileHover: { 
        boxShadow: '0 0 20px rgba(var(--primary-rgb), 0.3)' 
      },
      transition: { duration: 0.2 }
    }
}